import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const drawerSectionId = 'cart-drawer-section';
const drawerDebugPrefix = '[FILL DRAWER DEBUG]';
const defaultErrorMessage = "We couldn't update your bag. Please try again.";

function parseJson(text) {
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

function getErrorMessage(error) {
  const data = error?.data || error;

  if (typeof data?.description === 'string' && data.description.trim()) return data.description;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.errors === 'string' && data.errors.trim()) return data.errors;
  if (data?.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors).flat().find(Boolean);
    if (typeof first === 'string' && first.trim()) return first;
  }

  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  return defaultErrorMessage;
}

/** Manages the editable cart drawer, including Look-line grouping. */
class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  #hasCheckedInitialState = false;
  #bundleRemovalPending = false;

  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#ensureClosedAfterRestore();
    this.#groupLookLines();
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    if (this.#themeDrawer?.hasAttribute('open')) this.#handleDrawerOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  #handleClick = (event) => {
    const remove = event.target.closest?.('[data-fill-cart-drawer-bundle-component-remove]');
    if (!remove || !this.contains(remove)) return;

    event.preventDefault();
    this.#removeBundleComponent(remove);
  };

  #getBundleConversion(remove) {
    const component = remove.closest('[data-fill-cart-drawer-bundle-component]');
    const bundle = component?.closest('[data-fill-cart-drawer-bundle]');
    const parentKey = bundle?.dataset.fillCartDrawerBundleParentKey?.trim();

    if (!component || !bundle || !parentKey) throw new Error(defaultErrorMessage);

    const items = Array.from(bundle.querySelectorAll('[data-fill-cart-drawer-bundle-component]'))
      .filter((item) => item !== component)
      .map((item) => ({
        id: Number.parseInt(item.dataset.fillCartDrawerBundleVariantId || '', 10),
        quantity: Number.parseInt(item.dataset.fillCartDrawerBundleQuantity || '', 10),
      }));

    if (
      !items.length ||
      items.some((item) => !Number.isFinite(item.id) || !Number.isFinite(item.quantity) || item.quantity < 1)
    ) {
      throw new Error(defaultErrorMessage);
    }

    return {
      parentKey,
      name: bundle.dataset.fillCartDrawerBundleName?.trim() || 'Shop the Look',
      parent: bundle.dataset.fillCartDrawerBundleParent?.trim() || parentKey,
      image: bundle.dataset.fillCartDrawerBundleImage?.trim() || '',
      label: bundle.dataset.fillCartDrawerBundleLabel?.trim() || 'Complete Look',
      items,
    };
  }

  async #getCart() {
    const url = `${Theme.routes.cart_url}.js`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    });
    const responseText = await response.clone().text();
    const data = parseJson(responseText);

    console.log(`${drawerDebugPrefix} /cart.js`, {
      request: { url, method: 'GET', payload: null },
      response: { status: response.status, ok: response.ok, body: responseText, parsedJson: data },
      succeeded: response.ok && Array.isArray(data?.items),
    });

    if (!response.ok || !Array.isArray(data?.items)) {
      const error = new Error(getErrorMessage(data));
      error.data = data;
      throw error;
    }

    return data;
  }

  async #requestCart(url, body) {
    const payload = JSON.stringify(body);
    const label = url.includes('/cart/add') ? '/cart/add.js' : url.includes('/cart/change') ? '/cart/change.js' : '/cart/update.js';
    let response;
    let responseText = '';

    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: payload,
        credentials: 'same-origin',
      });
      responseText = await response.clone().text();
      const data = parseJson(responseText);

      console.log(`${drawerDebugPrefix} ${label}`, {
        request: { url, method: 'POST', payload: body },
        response: { status: response.status, ok: response.ok, body: responseText, parsedJson: data },
        succeeded: response.ok && !data?.errors && !data?.status,
      });

      if (!response.ok || data?.errors || data?.status) {
        const error = new Error(getErrorMessage(data));
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      console.error(`${drawerDebugPrefix} ${label} failed`, {
        request: { url, method: 'POST', payload: body },
        response: response
          ? { status: response.status, ok: response.ok, body: responseText, parsedJson: parseJson(responseText) }
          : null,
        error,
      });
      throw error;
    }
  }

  #getDrawerSectionHtml(addResponse) {
    const sectionHtml = addResponse?.sections?.[drawerSectionId];
    const sectionSelector = `#shopify-section-${drawerSectionId}`;
    const sectionDocument = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
    const hasExpectedWrapper = Boolean(sectionDocument.querySelector(sectionSelector));

    console.log(`${drawerDebugPrefix} section response`, {
      sectionId: drawerSectionId,
      sectionsUrl: window.location.pathname,
      selector: sectionSelector,
      returnedSectionHtml: sectionHtml || '',
      hasExpectedWrapper,
    });

    if (!sectionHtml || !hasExpectedWrapper) throw new Error(defaultErrorMessage);
    return sectionHtml;
  }

  #setComponentRemoveBusy(remove, isBusy) {
    if (isBusy) {
      remove.dataset.fillCartDrawerRemoveLabel = remove.textContent.trim();
      remove.textContent = 'Removing…';
      remove.disabled = true;
      remove.setAttribute('aria-busy', 'true');
      return;
    }

    remove.textContent = remove.dataset.fillCartDrawerRemoveLabel || 'Remove';
    remove.disabled = false;
    remove.removeAttribute('aria-busy');
  }

  #showBundleError(remove, error) {
    const line = remove.closest('.fill-cart-drawer__line');
    const container = line?.querySelector('.fill-cart-drawer__line-error');
    const message = container?.querySelector('.fill-cart-drawer__line-error-text');
    if (message) message.textContent = getErrorMessage(error);
    container?.classList.remove('hidden');
  }

  #clearBundleError(remove) {
    const line = remove.closest('.fill-cart-drawer__line');
    const container = line?.querySelector('.fill-cart-drawer__line-error');
    const message = container?.querySelector('.fill-cart-drawer__line-error-text');
    if (message) message.textContent = '';
    container?.classList.add('hidden');
  }

  #dispatchDrawerCartUpdate(cart, sections) {
    const sectionSelector = `#shopify-section-${drawerSectionId}`;
    console.log(`${drawerDebugPrefix} Drawer hydration dispatch`, {
      sectionId: drawerSectionId,
      selector: sectionSelector,
      existingTargetFound: Boolean(document.querySelector(sectionSelector)),
      returnedSectionHtml: sections?.[drawerSectionId] || '',
    });

    const event = new Event(StandardEvents.cartLinesUpdate, { bubbles: true });
    event.promise = Promise.resolve({
      cart: { totalQuantity: cart.item_count },
      detail: {
        itemCount: cart.item_count,
        items: cart.items,
        sections,
        source: 'fill-cart-drawer-bundle-component',
        didError: false,
      },
    });
    document.dispatchEvent(event);
  }

  async #restoreBundleParent(parentSnapshot, lookId) {
    const cartForRollback = await this.#getCart();
    const rollbackLineKeys = cartForRollback.items
      .filter((item) => item.properties?._look_id === lookId)
      .map((item) => item.key);

    if (rollbackLineKeys.length) {
      await this.#requestCart(Theme.routes.cart_update_url, {
        updates: Object.fromEntries(rollbackLineKeys.map((key) => [key, 0])),
      });
    }

    await this.#requestCart(Theme.routes.cart_add_url, {
      items: [
        {
          id: parentSnapshot.variantId,
          quantity: parentSnapshot.quantity,
          properties: parentSnapshot.properties,
        },
      ],
    });
  }

  async #removeBundleComponent(remove) {
    if (this.#bundleRemovalPending) return;

    let conversion;
    try {
      conversion = this.#getBundleConversion(remove);
    } catch (error) {
      console.error(`${drawerDebugPrefix} component removal could not be prepared`, { error, remove });
      this.#showBundleError(remove, error);
      return;
    }

    this.#bundleRemovalPending = true;
    this.#setComponentRemoveBusy(remove, true);
    this.#clearBundleError(remove);

    const lookId = `native-look-${conversion.parentKey}`;
    const properties = {
      _look_id: lookId,
      _look_name: conversion.name,
      _look_parent: conversion.parent,
      _look_image: conversion.image,
      _look_label: conversion.label,
    };
    let parentRemoved = false;
    let componentsAdded = false;
    let parentSnapshot = null;

    console.groupCollapsed(`${drawerDebugPrefix} component removal`);
    try {
      const cart = await this.#getCart();
      const parentLine = cart.items.find((item) => item.key === conversion.parentKey);
      if (!parentLine) throw new Error('Your bag changed before this item could be removed. Please try again.');

      const parentVariantId = Number.parseInt(parentLine.variant_id || parentLine.id || '', 10);
      const parentQuantity = Number.parseInt(parentLine.quantity || '', 10);
      if (!Number.isFinite(parentVariantId) || !Number.isFinite(parentQuantity) || parentQuantity < 1) {
        throw new Error('Your bundle could not be safely converted. Please try again.');
      }

      parentSnapshot = {
        variantId: parentVariantId,
        key: parentLine.key,
        quantity: parentQuantity,
        properties: { ...(parentLine.properties || {}) },
      };
      console.log(`${drawerDebugPrefix} native bundle parent snapshot`, parentSnapshot);
      console.log(`${drawerDebugPrefix} remaining component variants`, {
        items: conversion.items.map((item) => ({ ...item, properties })),
      });

      await this.#requestCart(Theme.routes.cart_change_url, {
        id: conversion.parentKey,
        quantity: 0,
        sections: drawerSectionId,
        sections_url: window.location.pathname,
      });
      parentRemoved = true;

      const addResponse = await this.#requestCart(Theme.routes.cart_add_url, {
        items: conversion.items.map((item) => ({ ...item, properties })),
        sections: drawerSectionId,
        sections_url: window.location.pathname,
      });
      componentsAdded = true;

      const sectionHtml = this.#getDrawerSectionHtml(addResponse);
      const cartAfterAdd = await this.#getCart();
      this.#dispatchDrawerCartUpdate(cartAfterAdd, { [drawerSectionId]: sectionHtml });
    } catch (error) {
      if (parentRemoved && !componentsAdded && parentSnapshot) {
        try {
          console.warn(`${drawerDebugPrefix} restoring native bundle after standalone add failure`, { parentSnapshot });
          await this.#restoreBundleParent(parentSnapshot, lookId);
        } catch (rollbackError) {
          console.error(`${drawerDebugPrefix} bundle-conversion rollback failed`, { parentSnapshot, rollbackError });
        }
      }

      console.error(`${drawerDebugPrefix} component removal failed`, {
        parentRemoved,
        componentsAdded,
        parentSnapshot,
        remainingItems: conversion.items,
        properties,
        error,
      });
      this.#showBundleError(remove, error);
    } finally {
      this.#setComponentRemoveBusy(remove, false);
      this.#bundleRemovalPending = false;
      console.groupEnd();
    }
  }

  #handleDrawerOpen = () => {
    this.#groupLookLines();
    this.#updateStickyState();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleCartLinesUpdate = (event) => {
    const isNormalProductAdd = event.context === 'product';
    const shouldAutoOpen = event.action === 'add' && !this.#themeDrawer?.isOpen && (this.hasAttribute('auto-open') || isNormalProductAdd);
    const shouldRedirectToCart = event.action === 'add' && isMobileBreakpoint();
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(async ({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              this.#groupLookLines();
              this.#updateStickyState();
            })
          );
        if (detail?.didError) {
          settle();
          return;
        }

        if (shouldRedirectToCart) {
          window.location.assign(Theme.routes.cart_url);
          return;
        }

        if (!shouldAutoOpen) {
          settle();
          return;
        }

        const openAndSettle = async () => {
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          this.#groupLookLines();
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) sourceModal.addEventListener('close', openAndSettle, { once: true });
        else void openAndSettle();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #ensureClosedAfterRestore() {
    if (this.#hasCheckedInitialState) return;
    this.#hasCheckedInitialState = true;
    this.#clearPersistedCartDrawerState();

    const drawer = this.#themeDrawer;
    if (!drawer?.hasAttribute('open')) return;
    if (!['loading', 'interactive'].includes(document.readyState)) return;
    const dialog = this.#dialog;
    drawer.removeAttribute('open');
    drawer.style.removeProperty('--drawer-stack-order');
    dialog?.classList.remove('theme-drawer__dialog-instant', 'theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start', 'theme-drawer__dialog--closing');
    if (dialog?.open) dialog.close();
    dialog?.removeAttribute('open');
    if (!document.querySelector('theme-drawer[open]')) document.querySelector('.page-wrapper')?.classList.remove('page-wrapper--drawer-open');
  }

  #clearPersistedCartDrawerState() {
    try {
      const savedDrawers = sessionStorage.getItem('theme-drawer-open');
      if (!savedDrawers) return;
      const nextDrawers = savedDrawers.split(',').filter(Boolean).filter((drawerId) => drawerId !== 'cart-drawer');
      if (nextDrawers.length) sessionStorage.setItem('theme-drawer-open', nextDrawers.join(','));
      else sessionStorage.removeItem('theme-drawer-open');
    } catch (error) {
      console.warn('[cart-drawer] Unable to clear persisted drawer state:', error);
    }
  }

  #groupLookLines() {
    const lines = /** @type {HTMLUListElement | null} */ (this.querySelector('[data-cart-drawer-lines]'));
    if (!lines) return;
    const lookLines = Array.from(lines.querySelectorAll('[data-fill-cart-drawer-look-id]')).filter(
      (line) => line instanceof HTMLLIElement && line.parentElement === lines
    );
    if (!lookLines.length) return;

    const groupedLines = new Map();
    for (const line of lookLines) {
      const lookId = line.dataset.fillCartDrawerLookId;
      if (!lookId) continue;
      const group = groupedLines.get(lookId) ?? [];
      group.push(line);
      groupedLines.set(lookId, group);
    }

    for (const [lookId, groupLines] of groupedLines) {
      const firstLine = groupLines[0];
      if (!firstLine || groupLines.some((line) => line.closest('[data-fill-cart-drawer-look-group]'))) continue;
      const lookName = firstLine.dataset.fillCartDrawerLookName || 'Shop the Look';
      const lookParent = firstLine.dataset.fillCartDrawerLookParent || '';
      const headingId = `CartDrawerLook-${lookId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
      const groupItem = document.createElement('li');
      groupItem.className = 'fill-cart-drawer__look-group';
      groupItem.dataset.fillCartDrawerLookGroup = lookId;
      const groupCard = document.createElement('section');
      groupCard.className = 'fill-cart-drawer__look-card';
      groupCard.setAttribute('aria-labelledby', headingId);
      const header = document.createElement('header');
      header.className = 'fill-cart-drawer__look-header';
      const copy = document.createElement('div');
      const label = document.createElement('p');
      label.className = 'fill-cart-drawer__look-label';
      label.textContent = 'Shop the Look';
      const title = document.createElement('h3');
      title.className = 'fill-cart-drawer__look-title';
      title.id = headingId;
      title.textContent = lookName;
      const count = document.createElement('p');
      count.className = 'fill-cart-drawer__look-count';
      count.textContent = `${groupLines.length} ${groupLines.length === 1 ? 'piece' : 'pieces'}`;
      const list = document.createElement('ul');
      list.className = 'fill-cart-drawer__look-items';
      list.setAttribute('role', 'list');
      copy.append(label, title);
      header.append(copy, count);
      groupCard.append(header, list);
      if (lookParent) groupItem.dataset.fillCartDrawerLookParent = lookParent;
      groupItem.append(groupCard);
      lines.insertBefore(groupItem, firstLine);
      for (const line of groupLines) {
        line.classList.add('fill-cart-drawer__line--look-child');
        list.append(line);
      }
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
    const ratio = summary.getBoundingClientRect().height / dialog.getBoundingClientRect().height;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) customElements.define('cart-drawer-component', CartDrawerComponent);
